/**
 * Liquid Glass Button Component
 * Implements Apple's Liquid Glass design principles for interactive elements
 * 
 * Features:
 * - Adaptive transparency and blur
 * - Motion-responsive interactions
 * - Accessibility compliance
 * - Real-time depth effects
 */

import { forwardRef } from 'react'
import { Button, type ButtonProps, useTheme } from '@mui/material'
import { 
  createLiquidGlassEffect, 
  liquidGlass,
  type LiquidGlassVariant,
  type MotionPreference 
} from '../../tokens'

export interface LiquidGlassButtonProps extends Omit<ButtonProps, 'variant'> {
  /**
   * Glass effect variant
   * @default 'regular'
   */
  glassVariant?: LiquidGlassVariant
  
  /**
   * Button style variant. Accepts the MUI variants plus semantic aliases
   * (`primary`/`secondary`/`danger`/`ghost`) used across the app, which are
   * translated to an MUI variant + color internally.
   * @default 'contained'
   */
  variant?: 'contained' | 'outlined' | 'text' | 'primary' | 'secondary' | 'danger' | 'ghost'
  
  /**
   * Enable enhanced glow effect
   * @default true
   */
  enhancedGlow?: boolean
  
  /**
   * Motion preference override
   */
  motionPreference?: MotionPreference
  
  /**
   * Custom glass color overlay
   */
  glassColor?: string
}

/**
 * LiquidGlassButton - A button component with Apple's Liquid Glass effects
 * 
 * @example
 * ```tsx
 * <LiquidGlassButton glassVariant="prominent" variant="contained">
 *   Sign In
 * </LiquidGlassButton>
 * ```
 */
export const LiquidGlassButton = forwardRef<HTMLButtonElement, LiquidGlassButtonProps>(
  ({
    glassVariant = 'regular',
    variant = 'contained',
    enhancedGlow = true,
    motionPreference,
    glassColor,
    children,
    sx,
    ...props
  }, ref) => {
    const theme = useTheme()
    const mode = theme.palette.mode

    // Translate semantic variant aliases to an MUI variant + color pairing.
    const SEMANTIC_VARIANTS: Record<string, { mui: 'contained' | 'outlined' | 'text'; color?: ButtonProps['color'] }> = {
      primary: { mui: 'contained', color: 'primary' },
      secondary: { mui: 'contained', color: 'secondary' },
      danger: { mui: 'contained', color: 'error' },
      ghost: { mui: 'text', color: 'inherit' },
    }
    const semantic = SEMANTIC_VARIANTS[variant]
    const muiVariant: 'contained' | 'outlined' | 'text' = semantic ? semantic.mui : (variant as 'contained' | 'outlined' | 'text')
    const muiColor = semantic?.color ?? props.color

    // Detect motion preference if not provided
    const finalMotionPreference = motionPreference || 
      (typeof window !== 'undefined' && window.matchMedia('(prefers-reduced-motion: reduce)').matches 
        ? 'reduced' 
        : 'full')
    
    // Create base glass effect
    const baseGlassStyles = createLiquidGlassEffect(
      glassVariant, 
      'surface', 
      mode, 
      finalMotionPreference, 
      {
        interactive: true,
        glowOnHover: enhancedGlow,
        scaleOnHover: true,
      }
    )
    
    // Enhanced button-specific styles
    const buttonGlassStyles = {
      ...baseGlassStyles,
      
      // Button-specific enhancements
      minHeight: '44px', // Accessibility: minimum touch target
      fontWeight: 600,
      letterSpacing: '0.02em',
      
      // Enhanced glass effect for buttons
      background: glassColor || (muiVariant === 'contained' 
        ? `linear-gradient(135deg, ${theme.palette.primary.main}CC, ${theme.palette.primary.dark}E6)`
        : 'transparent'),
      
      // Enhanced interaction states
      '&:hover': {
        ...baseGlassStyles['&:hover'],
        background: glassColor || (muiVariant === 'contained'
          ? `linear-gradient(135deg, ${theme.palette.primary.main}E6, ${theme.palette.primary.dark}F0)`
          : `rgba(${theme.palette.primary.main.replace('#', '')}, 0.1)`),
        
        // Enhanced glow for buttons
        ...(enhancedGlow && finalMotionPreference !== 'none' && {
          boxShadow: `
            ${baseGlassStyles.boxShadow}, 
            0 0 20px ${liquidGlass.motion.glow.color[mode]},
            0 0 40px ${liquidGlass.motion.glow.color[mode]}40
          `,
        }),
      },
      
      '&:active': {
        ...baseGlassStyles['&:active'],
        background: glassColor || (muiVariant === 'contained'
          ? `linear-gradient(135deg, ${theme.palette.primary.dark}E6, ${theme.palette.primary.main}F0)`
          : `rgba(${theme.palette.primary.main.replace('#', '')}, 0.15)`),
      },
      
      '&:focus-visible': {
        ...baseGlassStyles['&:focus-visible'],
        outline: 'none',
        boxShadow: `
          ${baseGlassStyles.boxShadow}, 
          0 0 0 3px ${theme.palette.primary.main}60,
          0 0 0 6px ${theme.palette.primary.main}30
        `,
      },
      
      // Disabled state
      '&:disabled': {
        opacity: 0.6,
        transform: 'none',
        backdropFilter: finalMotionPreference === 'none' ? 'none' : 'blur(4px)',
        background: mode === 'dark' 
          ? 'rgba(255, 255, 255, 0.05)' 
          : 'rgba(0, 0, 0, 0.05)',
        color: theme.palette.text.disabled,
        cursor: 'not-allowed',
      },
      
      // Outlined variant specific styles
      ...(muiVariant === 'outlined' && {
        border: `2px solid ${theme.palette.primary.main}80`,
        background: 'transparent',
        color: theme.palette.primary.main,
        
        '&:hover': {
          border: `2px solid ${theme.palette.primary.main}`,
          background: `${theme.palette.primary.main}10`,
        },
      }),
      
      // Text variant specific styles
      ...(muiVariant === 'text' && {
        background: 'transparent',
        border: 'none',
        color: theme.palette.primary.main,
        
        '&:hover': {
          background: `${theme.palette.primary.main}10`,
        },
      }),
    }
    
    return (
      <Button
        ref={ref}
        variant={muiVariant}
        sx={{
          ...buttonGlassStyles,
          ...sx,
        }}
        {...props}
        color={muiColor}
      >
        {children}
      </Button>
    )
  }
)

LiquidGlassButton.displayName = 'LiquidGlassButton'

export default LiquidGlassButton
